import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { AnimatePresence, m, useReducedMotion } from "motion/react";
import { AdminShell } from "../../components/admin/AdminShell";
import { StatusBadge } from "../../components/admin/StatusBadge";
import { Button } from "../../components/Button";
import { questions } from "../../config/questions";
import { getAnswerLabels } from "../../lib/generateSnapshot";
import { adminMutation, apiJson } from "../../lib/adminApi";
import type { AdminOwner, SubmissionDetail } from "../../types/admin";
import type { SubmissionStatus } from "../../types/friction-finder";

type DetailResult = { submission: SubmissionDetail; activeAdmins: AdminOwner[] };
const statuses: SubmissionStatus[] = ["New", "Reviewing", "Contacted", "Closed"];
function dateTime(value: string) { return new Intl.DateTimeFormat("en-AU", { dateStyle: "long", timeStyle: "short" }).format(new Date(value)); }

function words(value: string) {
  const spaced = value.replace(/([a-z])([A-Z])/g, "$1 $2").replace(/_/g, " ");
  return spaced.charAt(0).toUpperCase() + spaced.slice(1);
}

function changePair(value: unknown): { from: unknown; to: unknown } | null {
  if (!value || typeof value !== "object" || !("from" in value) || !("to" in value)) return null;
  return value as { from: unknown; to: unknown };
}

function auditDetails(changeData: Record<string, unknown>) {
  const details: string[] = [];
  for (const [key, value] of Object.entries(changeData)) {
    const pair = changePair(value);
    if (key === "source" && typeof value === "string") {
      details.push(`Source: ${words(value)}.`);
    } else if (key === "status") {
      details.push(pair ? `Status changed from ${String(pair.from)} to ${String(pair.to)}.` : `Initial status: ${String(value)}.`);
    } else if (key === "assignedTo" || key === "assignedToAdminUserId") {
      if (key === "assignedToAdminUserId" || !pair) details.push(pair ? "Assigned owner changed." : `Assigned owner: ${String(value ?? "Unassigned")}.`);
      else details.push(`Assigned owner changed from ${String(pair.from ?? "Unassigned")} to ${String(pair.to ?? "Unassigned")}.`);
    } else if (key === "internalNotes") {
      const length = value && typeof value === "object" && "length" in value && typeof value.length === "number" ? value.length : null;
      details.push(length === null ? "Internal notes updated." : `Internal notes updated (${length} characters).`);
    } else if (pair) {
      details.push(`${words(key)} changed.`);
    } else if (["string", "number", "boolean"].includes(typeof value)) {
      details.push(`${words(key)}: ${String(value)}.`);
    } else {
      details.push(`${words(key)} updated.`);
    }
  }
  return details.length ? details : ["Audit details recorded."];
}

export function AdminSubmissionDetailPage() {
  const { id = "" } = useParams();
  const [result, setResult] = useState<DetailResult | null>(null);
  const [state, setState] = useState<"loading" | "ready" | "error">("loading");
  const [mutationState, setMutationState] = useState<"idle" | "saving" | "saved" | "error">("idle");
  const [notes, setNotes] = useState("");
  const reduceMotion = useReducedMotion();
  const stateTransition = { duration: reduceMotion ? 0 : 0.16, ease: [0.22, 1, 0.36, 1] as const };

  const load = async (showLoading = true) => {
    if (showLoading) setState("loading");
    try {
      const data = await apiJson<DetailResult>(`/api/admin/submissions/${id}`);
      setResult(data); setNotes(data.submission.internalNotes ?? ""); setState("ready");
    } catch { setState("error"); }
  };
  useEffect(() => { void load(); }, [id]);

  const update = async (changes: Record<string, unknown>) => {
    setMutationState("saving");
    try {
      await adminMutation(`/api/admin/submissions/${id}`, "PATCH", changes);
      await load(false);
      setMutationState("saved");
    } catch { setMutationState("error"); }
  };

  return (
    <AdminShell title="Submission details" backHref="/admin/submissions">
      {state === "loading" && <div className="admin-state admin-loading-state" role="status"><strong>Loading submission…</strong><span className="state-help">Retrieving the full record and audit history.</span><span className="loading-lines" aria-hidden="true"><i /><i /><i /></span></div>}
      {state === "error" && <div className="admin-state error-summary" role="alert"><strong>This submission could not be loaded.</strong><span className="state-help">Return to submissions and try again.</span></div>}
      {state === "ready" && result && (
        <m.div className="admin-detail-content" initial={reduceMotion ? false : { opacity: 0.8, y: 4 }} animate={{ opacity: 1, y: 0 }} transition={stateTransition}>
          <div className="detail-grid">
            <section className="admin-card contact-card" aria-labelledby="contact-details-heading">
              <div className="card-heading-row"><div><p className="section-label">Submission</p><h2 id="contact-details-heading">Contact details</h2></div><StatusBadge status={result.submission.status} /></div>
              <dl className="detail-list">
                <div><dt>Full name</dt><dd>{result.submission.fullName}</dd></div>
                <div><dt>Business name</dt><dd>{result.submission.businessName}</dd></div>
                <div><dt>Work email</dt><dd><a href={`mailto:${result.submission.workEmail}`}>{result.submission.workEmail}</a></dd></div>
                <div><dt>Submitted</dt><dd>{dateTime(result.submission.submittedAt)}</dd></div>
                <div><dt>Intent</dt><dd>{result.submission.intent}</dd></div>
                {result.submission.referrer && <div><dt>Referrer</dt><dd className="break-text">{result.submission.referrer}</dd></div>}
              </dl>
            </section>
            <section className="admin-card workflow-card" aria-labelledby="workflow-heading">
              <div className="card-heading-row"><div><p className="section-label">Internal only</p><h2 id="workflow-heading">Internal workflow</h2></div></div>
              <div className="field">
                <label htmlFor="detail-status">Status</label>
                <select id="detail-status" value={result.submission.status} disabled={mutationState === "saving"} onChange={(event) => void update({ status: event.target.value })}>
                  {statuses.map((status) => <option key={status}>{status}</option>)}
                </select>
              </div>
              <div className="field">
                <label htmlFor="detail-owner">Assigned owner</label>
                <select id="detail-owner" value={result.submission.assignedToAdminUser?.id ?? ""} disabled={mutationState === "saving"} onChange={(event) => void update({ assignedToAdminUserId: event.target.value || null })}>
                  <option value="">Unassigned</option>
                  {result.activeAdmins.map((admin) => <option value={admin.id} key={admin.id}>{admin.displayName}</option>)}
                </select>
              </div>
              <div className="field">
                <label htmlFor="internal-notes">Internal notes</label>
                <textarea id="internal-notes" rows={6} maxLength={5000} value={notes} onChange={(event) => setNotes(event.target.value)} />
                <p className="field-help">Visible only to authorised HuddleCo admins · {notes.length}/5000</p>
              </div>
              <Button disabled={mutationState === "saving" || notes === (result.submission.internalNotes ?? "")} onClick={() => void update({ internalNotes: notes || null })}>{mutationState === "saving" ? "Saving…" : "Save notes"}</Button>
              <div className="mutation-status" aria-live="polite">
                <AnimatePresence initial={false}>
                  {mutationState === "saved" && <m.p key="saved" className="mutation-message mutation-message--success" initial={reduceMotion ? false : { opacity: 0.01, y: -2 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }} transition={stateTransition}>Changes saved.</m.p>}
                  {mutationState === "error" && <m.p key="error" className="mutation-message mutation-message--error" initial={reduceMotion ? false : { opacity: 0.01, y: -2 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }} transition={stateTransition}>Changes could not be saved. Try again.</m.p>}
                </AnimatePresence>
              </div>
            </section>
          </div>

          <section className="admin-card detail-snapshot" aria-labelledby="detail-snapshot-heading">
            <div className="snapshot-heading-row"><div><p className="section-label">Generated output</p><h2 id="detail-snapshot-heading">Complete Friction Snapshot</h2></div><span className={`intent-badge intent-badge--${result.submission.snapshot.intent === "Fixing Today" ? "fixing" : "planning"}`}>{result.submission.snapshot.intent}</span></div>
            <p className="detail-summary">{result.submission.snapshot.summary}</p>
            <div className="snapshot-detail-grid">
              <div><h3>{result.submission.snapshot.impactHeading}</h3><p>{result.submission.snapshot.impactStatement}</p></div>
              <div><h3>{result.submission.snapshot.desiredStateHeading}</h3><p>{result.submission.snapshot.desiredStateStatement}</p></div>
              <div><h3>{result.submission.snapshot.nextStepHeading}</h3><p>{result.submission.snapshot.nextStepStatement}</p></div>
            </div>
          </section>

          <section className="admin-card" aria-labelledby="answers-heading">
            <h2 id="answers-heading">All assessment answers</h2>
            <div className="admin-answers">
              {questions.map((question) => <div key={question.id}><h3>{question.heading}</h3><p>{getAnswerLabels(question.id, result.submission.answers).join(", ")}</p></div>)}
            </div>
          </section>

          <section className="admin-card" aria-labelledby="record-heading">
            <h2 id="record-heading">Record details</h2>
            <dl className="detail-list compact"><div><dt>Created</dt><dd>{dateTime(result.submission.createdAt)}</dd></div><div><dt>Last updated</dt><dd>{dateTime(result.submission.updatedAt)}</dd></div></dl>
          </section>

          <section className="admin-card" aria-labelledby="audit-heading">
            <h2 id="audit-heading">Audit history</h2>
            <ol className="audit-list">
              {result.submission.auditEvents.map((event) => (
                <li key={event.id}>
                  <div><strong>{event.eventType === "submission_created" ? "Submission created" : "Submission updated"}</strong><span>{event.actorAdminUser?.displayName ?? "System"} · {dateTime(event.createdAt)}</span></div>
                  <ul className="audit-details">
                    {auditDetails(event.changeData).map((detail) => <li key={detail}>{detail}</li>)}
                  </ul>
                </li>
              ))}
            </ol>
          </section>
        </m.div>
      )}
    </AdminShell>
  );
}
